// frontend/src/pages/admin/Profile.js
import React, { useState, useEffect } from 'react';
import axios from '../../utils/axios';
import { toast } from 'react-toastify';
import {
  FiUser,
  FiMail,
  FiPhone,
  FiLock,
  FiSave,
  FiCamera
} from 'react-icons/fi';

const Profile = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Profile form
  const [profileForm, setProfileForm] = useState({
    full_name: '',
    email: '',
    phone: ''
  });

  // Password form
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const [showPasswordForm, setShowPasswordForm] = useState(false);

  useEffect(() => {
    fetchUser();
  }, []);

  const fetchUser = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/auth/me');
      if (response.data.success) {
        const userData = response.data.data.user;
        setUser(userData);
        setProfileForm({
          full_name: userData.full_name || '',
          email: userData.email || '',
          phone: userData.phone || ''
        });
      }
    } catch (error) {
      console.error('Fetch user error:', error);
      toast.error('Мэдээлэл татахад алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    
    if (!profileForm.full_name || !profileForm.email) {
      toast.warning('Нэр болон и-мэйл заавал шаардлагатай');
      return;
    }

    setSaving(true);
    try {
      const response = await axios.put('/api/auth/profile', profileForm);
      
      if (response.data.success) {
        toast.success('Мэдээлэл амжилттай шинэчлэгдлээ');
        fetchUser();
      }
    } catch (error) {
      console.error('Update profile error:', error);
      toast.error(error.response?.data?.message || 'Алдаа гарлаа');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (!passwordForm.current_password || !passwordForm.new_password) {
      toast.warning('Бүх талбарыг бөглөнө үү');
      return;
    }

    if (passwordForm.new_password.length < 6) {
      toast.warning('Нууц үг хамгийн багадаа 6 тэмдэгттэй байх ёстой');
      return;
    }

    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.warning('Нууц үг таарахгүй байна');
      return;
    }

    setSaving(true);
    try {
      const response = await axios.put('/api/auth/password', {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });

      if (response.data.success) {
        toast.success('Нууц үг амжилттай солигдлоо');
        setPasswordForm({
          current_password: '',
          new_password: '',
          confirm_password: ''
        });
        setShowPasswordForm(false);
      }
    } catch (error) {
      console.error('Change password error:', error);
      toast.error(error.response?.data?.message || 'Алдаа гарлаа');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="spinner-lg mb-4"></div>
          <p className="text-gray-600">Уншиж байна...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Хувийн мэдээлэл</h1>
        <p className="text-gray-600 mt-1">Өөрийн мэдээллээ засах</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Card */}
        <div className="lg:col-span-1">
          <div className="card text-center">
            <div className="card-body">
              {/* Avatar */}
              <div className="relative inline-block mb-4">
                <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                  <span className="text-blue-700 font-bold text-3xl">
                    {user.full_name?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <button
                  className="absolute bottom-0 right-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors"
                  title="Зураг солих"
                >
                  <FiCamera size={16} />
                </button>
              </div>

              <h3 className="text-lg font-semibold text-gray-900">{user.full_name}</h3>
              <p className="text-sm text-gray-500 capitalize">{user.role_display || user.role_name}</p>
              
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="text-left space-y-2">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <FiMail size={16} />
                    <span>{user.email}</span>
                  </div>
                  {user.phone && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <FiPhone size={16} />
                      <span>{user.phone}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Forms */}
        <div className="lg:col-span-2 space-y-6">
          {/* Profile Form */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Мэдээлэл засах</h3>
            </div>
            <div className="card-body">
              <form onSubmit={handleProfileSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Нэр <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FiUser className="text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={profileForm.full_name}
                      onChange={(e) => setProfileForm({ ...profileForm, full_name: e.target.value })}
                      className="input pl-10"
                      placeholder="Нэр"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    И-мэйл <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FiMail className="text-gray-400" />
                    </div>
                    <input
                      type="email"
                      value={profileForm.email}
                      onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                      className="input pl-10"
                      placeholder="example@email.com"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Утасны дугаар
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FiPhone className="text-gray-400" />
                    </div>
                    <input
                      type="tel"
                      value={profileForm.phone}
                      onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                      className="input pl-10"
                      placeholder="99001122"
                    />
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={saving}
                    className="btn btn-primary"
                  >
                    {saving ? (
                      <>
                        <div className="spinner mr-2"></div>
                        Хадгалж байна...
                      </>
                    ) : (
                      <>
                        <FiSave className="mr-2" />
                        Хадгалах
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Password Form */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Нууц үг солих</h3>
            </div>
            <div className="card-body">
              {!showPasswordForm ? (
                <button
                  onClick={() => setShowPasswordForm(true)}
                  className="btn btn-secondary"
                >
                  <FiLock className="mr-2" />
                  Нууц үг солих
                </button>
              ) : (
                <form onSubmit={handlePasswordSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Одоогийн нууц үг <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      value={passwordForm.current_password}
                      onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                      className="input"
                      placeholder="••••••••"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Шинэ нууц үг <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      value={passwordForm.new_password}
                      onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                      className="input"
                      placeholder="••••••••"
                      minLength="6"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">Хамгийн багадаа 6 тэмдэгт</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Нууц үг давтах <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      value={passwordForm.confirm_password}
                      onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                      className="input"
                      placeholder="••••••••"
                      required
                    />
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowPasswordForm(false);
                        setPasswordForm({
                          current_password: '',
                          new_password: '',
                          confirm_password: ''
                        });
                      }}
                      className="btn btn-secondary"
                    >
                      Болих
                    </button>
                    <button
                      type="submit"
                      disabled={saving}
                      className="btn btn-primary"
                    >
                      {saving ? (
                        <>
                          <div className="spinner mr-2"></div>
                          Солиж байна...
                        </>
                      ) : (
                        <>
                          <FiLock className="mr-2" />
                          Нууц үг солих
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;